/**
 * DateField — a fully custom, premium calendar picker (no native browser popup).
 * Renders a branded field that opens an anchored calendar dropdown: month nav,
 * weekday row, accent-filled selected day, today ring, hover halos, and
 * Today/Clear actions. Works identically on web and native (RN Modal + backdrop
 * close). Value is always an ISO YYYY-MM-DD string, matching the backend.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Platform, Pressable, Text, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import SkIcon from './skIcon';
import { showToast } from './skToast';

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const pad = (n: number) => String(n).padStart(2, '0');
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return isNaN(d.getTime()) ? null : d;
};
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Earliest selectable date (ISO YYYY-MM-DD). Earlier days are dimmed and
   *  tapping one shows a toast instead of selecting. */
  min?: string;
  /** Toast headline shown when an earlier-than-min day is tapped. */
  minMessage?: string;
}

/** One day cell with a hover halo. `disabled` days (before min) are dimmed. */
const DayCell: React.FC<{ date: Date; inMonth: boolean; selected: boolean; today: boolean; disabled: boolean; cell: number; onPress: () => void }> = ({ date, inMonth, selected, today, disabled, cell, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [h, setH] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      style={{
        width: cell,
        height: cell,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 9,
        opacity: disabled ? 0.4 : 1,
        backgroundColor: selected ? colors.accent : h && !selected && !disabled ? colors.paper2 : 'transparent',
        borderWidth: today && !selected ? 1 : 0,
        borderColor: colors.accentLine,
        ...web({
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background-color .1s ease',
          ...(selected ? { backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: `0 4px 10px -3px ${colors.accent}99` } : {}),
        }),
      }}
      {...(isWeb ? { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) } : {})}
    >
      <Text style={{ fontFamily: selected ? font.sansSemibold : font.sansMedium, fontSize: 13, fontWeight: selected ? '600' : '500', color: selected ? '#fff' : inMonth ? colors.ink : colors.ink4 }}>
        {date.getDate()}
      </Text>
    </Pressable>
  );
};

const DateField: React.FC<Props> = ({ value, onChange, placeholder = 'Select date', min, minMessage }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const triggerRef = useRef<View>(null);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null);
  const [view, setView] = useState<{ y: number; mo: number }>(() => {
    const d = parse(value) || new Date();
    return { y: d.getFullYear(), mo: d.getMonth() };
  });

  const selectedDate = parse(value);
  const today = new Date();
  const active = open || hover;

  const display = selectedDate ? `${selectedDate.getDate()} ${MONTHS_SHORT[selectedDate.getMonth()]} ${selectedDate.getFullYear()}` : placeholder;

  const openMenu = () => {
    const base = parse(value) || new Date();
    setView({ y: base.getFullYear(), mo: base.getMonth() });
    const node = triggerRef.current as any;
    if (node?.measureInWindow) {
      node.measureInWindow((x: number, y: number, _w: number, h: number) => {
        setAnchor({ top: y + h + 6, left: x, width: 40 * 7 + 28 });
        setOpen(true);
      });
    } else {
      setAnchor({ top: 0, left: 0, width: 40 * 7 + 28 });
      setOpen(true);
    }
  };
  const close = () => setOpen(false);

  // Close the calendar on scroll — it's anchored to the field's on-screen
  // position, so scrolling would otherwise detach it.
  useEffect(() => {
    if (!open || !isWeb || typeof window === 'undefined') return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  const minDate = parse(min || '');
  const pickDate = (d: Date) => {
    if (minDate && d.getTime() < minDate.getTime()) {
      showToast('warn', minMessage || 'Choose a later date', `Pick a date on or after ${minDate.getDate()} ${MONTHS_SHORT[minDate.getMonth()]} ${minDate.getFullYear()}.`);
      return; // keep the calendar open so they can pick a valid day
    }
    onChange(toISO(d));
    close();
  };
  const shift = (delta: number) => setView((v) => {
    const m = v.mo + delta;
    return { y: v.y + Math.floor(m / 12), mo: ((m % 12) + 12) % 12 };
  });

  // Build a 6-week (42-cell) grid for the displayed month.
  const first = new Date(view.y, view.mo, 1);
  const startDow = first.getDay();
  const cells: Date[] = [];
  for (let i = 0; i < startDow; i++) cells.push(new Date(view.y, view.mo, 1 - startDow + i));
  const dim = new Date(view.y, view.mo + 1, 0).getDate();
  for (let d = 1; d <= dim; d++) cells.push(new Date(view.y, view.mo, d));
  while (cells.length < 42) { const last = cells[cells.length - 1]; cells.push(new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1)); }

  const CELL = 40;
  const GRID_W = CELL * 7; // exactly 7 columns so weekdays sit over dates
  const navBtn = (icon: string, onPress: () => void) => (
    <Pressable onPress={onPress} style={{ width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
      <SkIcon name={icon} size={15} color={colors.ink2} strokeWidth={2.2} />
    </Pressable>
  );

  return (
    <>
      <Pressable
        ref={triggerRef}
        onPress={openMenu}
        style={{
          height: 40,
          borderWidth: 1,
          borderColor: active ? colors.accent : colors.line,
          backgroundColor: colors.paper,
          borderRadius: 10,
          paddingLeft: 13,
          paddingRight: 5,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...web({ cursor: 'pointer', transition: 'border-color .14s ease, box-shadow .14s ease', boxShadow: open ? `0 0 0 3px ${colors.accent}22` : 'none' }),
        }}
        {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
      >
        <Text numberOfLines={1} style={{ flex: 1, fontFamily: font.sans, fontSize: 13, color: selectedDate ? colors.ink : colors.ink4 }}>{display}</Text>
        <View style={{ width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? colors.accentSoft : colors.paper2, borderWidth: 1, borderColor: active ? colors.accentLine : colors.line }}>
          <SkIcon name="calendar" size={15} color={active ? colors.accentInk : colors.ink3} strokeWidth={2} />
        </View>
      </Pressable>

      <Modal transparent visible={open} animationType="fade" onRequestClose={close}>
        <Pressable style={{ flex: 1 }} onPress={close}>
          {anchor && (
            <Pressable
              onPress={() => {}}
              style={{
                position: 'absolute',
                top: anchor.top,
                left: anchor.left,
                width: anchor.width,
                backgroundColor: colors.card,
                borderWidth: 1,
                borderColor: colors.line,
                borderRadius: 14,
                overflow: 'hidden',
                ...(isWeb ? ({ boxShadow: '0 16px 40px rgba(16,24,40,.22)' } as any) : { elevation: 12 }),
              }}
            >
              {/* header */}
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, paddingHorizontal: 14, borderBottomWidth: 1, borderColor: colors.line }}>
                <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>{MONTHS[view.mo]} {view.y}</Text>
                <View style={{ flexDirection: 'row', gap: 7 }}>
                  {navBtn('chevLeft', () => shift(-1))}
                  {navBtn('chevRight', () => shift(1))}
                </View>
              </View>

              {/* weekdays + grid — fixed 7-column width so days sit under weekdays */}
              <View style={{ paddingVertical: 12, alignItems: 'center' }}>
                <View style={{ width: GRID_W }}>
                  <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                    {DOW.map((d, i) => (
                      <Text key={i} style={{ width: CELL, textAlign: 'center', fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', color: colors.ink4, letterSpacing: 0.4 }}>{d}</Text>
                    ))}
                  </View>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', width: GRID_W }}>
                    {cells.map((d, i) => (
                      <DayCell
                        key={i}
                        date={d}
                        cell={CELL}
                        inMonth={d.getMonth() === view.mo}
                        selected={!!selectedDate && sameDay(d, selectedDate)}
                        today={sameDay(d, today)}
                        disabled={!!minDate && d.getTime() < minDate.getTime()}
                        onPress={() => pickDate(d)}
                      />
                    ))}
                  </View>
                </View>
              </View>

              {/* footer */}
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 11, paddingHorizontal: 14, borderTopWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2 }}>
                <Pressable onPress={() => { onChange(''); close(); }} style={web({ cursor: 'pointer' })}>
                  <Text style={{ fontFamily: font.sansMedium, fontSize: 12.5, fontWeight: '500', color: colors.ink3 }}>Clear</Text>
                </Pressable>
                <Pressable onPress={() => pickDate(new Date())} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, height: 30, paddingHorizontal: 12, borderRadius: 8, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, ...web({ cursor: 'pointer' }) }}>
                  <SkIcon name="calendar" size={13} color={colors.accentInk} strokeWidth={2.2} />
                  <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: colors.accentInk }}>Today</Text>
                </Pressable>
              </View>
            </Pressable>
          )}
        </Pressable>
      </Modal>
    </>
  );
};

export default DateField;
